import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useActivity } from '../App';

const apiCall = () => axios.create({
  baseURL: 'http://localhost:8080',
  headers: { Authorization: `Bearer ${localStorage.getItem('wb_admin_token')}` }
});

const Referrals = () => {
  const { logActivity } = useActivity?.() || {};
  const [data, setData]           = useState([]);
  const [stats, setStats]         = useState({ total: 0, totalCoinsGiven: 0, totalReferrals: 0 });
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [expanded, setExpanded]   = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await apiCall().get('/api/admin/referrals');
      const payload = res.data?.data || res.data || [];
      setData(Array.isArray(payload) ? payload : []);
      setStats({
        total: res.data?.total ?? (Array.isArray(payload) ? payload.length : 0),
        totalCoinsGiven: res.data?.totalCoinsGiven ?? 0,
        totalReferrals: res.data?.totalReferrals ?? 0,
      });
    } catch {
      toast.error('Failed to load referrals');
    } finally {
      setLoading(false);
    }
  };

  const filtered = data.filter(r =>
    !search ||
    r.ownerName?.toLowerCase().includes(search.toLowerCase()) ||
    r.code?.toLowerCase().includes(search.toLowerCase())
  );

  const topReferrer = [...data].sort((a, b) => (b.totalReferrals ?? 0) - (a.totalReferrals ?? 0))[0];

  const toggleExpand = (id) => setExpanded(prev => (prev === id ? null : id));

  return (
    <div className="animate-in">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="breadcrumb"><span>Users</span> / <span className="current">Referrals</span></div>
          <h1>👥 Referrals</h1>
          <p style={{ color: 'var(--text-secondary)' }}>Track referral codes, usage, and coin rewards</p>
        </div>
        <button style={primaryBtnS} onClick={loadData}>🔄 Refresh</button>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px', marginBottom: '24px' }}>
        {[
          { label: 'Total Codes',        value: stats.total,              color: 'var(--accent-primary)', icon: '🔑' },
          { label: 'Total Referrals',    value: stats.totalReferrals,     color: '#22c55e',               icon: '👤' },
          { label: 'Total Coins Given',  value: stats.totalCoinsGiven?.toLocaleString() ?? '0', color: '#f59e0b', icon: '🪙' },
          { label: 'Top Referrer',
            value: topReferrer ? topReferrer.ownerName : '—',
            sub: topReferrer ? `${topReferrer.totalReferrals} referrals` : '',
            color: '#8b5cf6', icon: '🏅' },
        ].map((s, i) => (
          <div key={i} style={cardS}>
            <div style={{ fontSize: '1.5rem' }}>{s.icon}</div>
            <div style={{ fontSize: s.label === 'Top Referrer' ? '1rem' : '1.5rem', fontWeight: 800, color: s.color, marginTop: '6px', wordBreak: 'break-word' }}>{s.value}</div>
            {s.sub && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '2px' }}>{s.sub}</div>}
            <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: '4px' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
        <input
          style={{ ...inputS, flex: 1 }}
          placeholder="Search by owner name or referral code…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <span style={countBadgeS}>{filtered.length} results</span>
      </div>

      {/* Table */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                {['#', 'Owner', 'Code', 'Total Referrals', 'Coins Earned', 'Created At', ''].map(h => (
                  <th key={h} style={thS}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={emptyS}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} style={emptyS}>No referrals found</td></tr>
              ) : filtered.map((r, i) => (
                <React.Fragment key={r.id}>
                  <tr
                    style={{ borderBottom: '1px solid var(--border-color)', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={tdMutedS}>{i + 1}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ fontSize: '0.86rem', fontWeight: 600 }}>{r.ownerName || '—'}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{r.ownerEmail || ''}</div>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={codeBadgeS}>{r.code}</span>
                    </td>
                    <td style={{ padding: '10px 14px', fontWeight: 700, color: 'var(--accent-primary)' }}>
                      {r.totalReferrals ?? 0}
                    </td>
                    <td style={{ padding: '10px 14px', fontWeight: 700, color: '#f59e0b' }}>
                      {(r.totalCoinsEarned ?? 0).toLocaleString()} 🪙
                    </td>
                    <td style={tdMutedS}>
                      {r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <button
                        style={{ ...actionBtnS, background: expanded === r.id ? 'var(--accent-primary)' : 'var(--bg-tertiary)', color: expanded === r.id ? 'white' : 'var(--text-primary)' }}
                        onClick={() => toggleExpand(r.id)}
                      >
                        {expanded === r.id ? '▲ Hide' : '▼ Uses'}
                      </button>
                    </td>
                  </tr>

                  {/* Expanded sub-table */}
                  {expanded === r.id && (
                    <tr>
                      <td colSpan={7} style={{ padding: '0', background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)' }}>
                        <div style={{ padding: '12px 24px 16px 24px' }}>
                          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.05em' }}>
                            Recent Uses
                          </div>
                          {!r.recentUses || r.recentUses.length === 0 ? (
                            <div style={{ color: 'var(--text-muted)', fontSize: '0.84rem', padding: '8px 0' }}>No uses yet</div>
                          ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                                  {['Referred User', 'Coins Awarded', 'Used At'].map(h => (
                                    <th key={h} style={{ ...thS, background: 'transparent', fontSize: '0.71rem' }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {r.recentUses.map((u, ui) => (
                                  <tr key={u.id ?? ui} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                    <td style={{ padding: '8px 14px', fontSize: '0.84rem' }}>{u.referredUserName || `User #${u.referredUserId}`}</td>
                                    <td style={{ padding: '8px 14px', fontWeight: 700, color: '#22c55e', fontSize: '0.84rem' }}>+{u.coinsAwarded ?? 0} 🪙</td>
                                    <td style={{ padding: '8px 14px', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                                      {u.usedAt ? new Date(u.usedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const cardS       = { background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '18px', textAlign: 'center' };
const inputS      = { padding: '9px 14px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '0.88rem', outline: 'none' };
const primaryBtnS = { padding: '9px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white' };
const actionBtnS  = { padding: '5px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, transition: 'all 0.15s' };
const countBadgeS = { padding: '9px 14px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', fontSize: '0.82rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' };
const codeBadgeS  = { fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 700, padding: '3px 9px', borderRadius: '6px', background: 'rgba(99,102,241,0.13)', color: 'var(--accent-primary)', letterSpacing: '0.06em' };
const thS         = { padding: '10px 14px', textAlign: 'left', fontSize: '0.73rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const tdMutedS    = { padding: '10px 14px', fontSize: '0.78rem', color: 'var(--text-muted)' };
const emptyS      = { textAlign: 'center', padding: '50px', color: 'var(--text-muted)' };

export default Referrals;
